import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconDocument, IconLoad } from '@Knowledge-OTP/znk-ui-components'
import { useDispatch } from 'react-redux'
import useTranslation from '../../../i18n/useTranslation'
import { showAlert } from '../../../common/Alert/util'
import useGetStatementAccount from '../useGetStatementAccount'
import {
  buildStatementPeriodParams,
  getEffectiveStudentIds,
  getFirstNonEmptyId,
  getSchoolYearSubtitle,
  parseDateInputAsUTC
} from './statementAccountUtils'
import './index.css'

const periods = [
  { value: 'all', title: 'All time', subtitle: 'Full history since enrollment — what parents ask for most often', recommended: true },
  { value: 'schoolYear', title: 'This school year' },
  { value: 'lastThreeMonths', title: 'Last 3 months' },
  { value: 'outstanding', title: 'Outstanding only', subtitle: 'Unpaid & overdue invoices, no payment history' },
  { value: 'custom', title: 'Custom range' }
]

const StatementAccount = ({ query }) => {
  const dispatch = useDispatch()
  const { translate } = useTranslation()
  const { downloadStatement, loading } = useGetStatementAccount()
  const rootRef = useRef(null)
  const fromRef = useRef(null)
  const toRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [period, setPeriod] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isGenerating = loading || isSubmitting
  const parentUserIds = query?.parentUserId
  const userIds = query?.userId
  const effectiveParentUserId = useMemo(() => getFirstNonEmptyId(parentUserIds), [parentUserIds])
  const effectiveStudentIds = useMemo(() => getEffectiveStudentIds(userIds), [userIds])
  const downloadDisabled = isGenerating

  const text = (key, fallback, params) => {
    const value = translate(key, params)
    return value === key ? fallback : value
  }

  const resetPanel = useCallback(() => {
    setIsOpen(false)
    setPeriod('all')
    setFromDate('')
    setToDate('')
    setErrors({})
  }, [])

  const closePanel = useCallback(() => {
    if (!isGenerating) resetPanel()
  }, [isGenerating, resetPanel])

  useEffect(() => {
    if (!isOpen) return undefined
    const onDocumentMouseDown = (event) => {
      if (!isGenerating && rootRef.current && !rootRef.current.contains(event.target)) closePanel()
    }
    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape' && !isGenerating) closePanel()
    }
    document.addEventListener('mousedown', onDocumentMouseDown)
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown)
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [isOpen, isGenerating, closePanel])

  const validateCustomRange = () => {
    if (period !== 'custom') return true
    const from = parseDateInputAsUTC(fromDate)
    const to = toDate ? parseDateInputAsUTC(toDate) : null
    const nextErrors = {}
    if (!from) nextErrors.from = text('payments.statement.validation.fromRequired', 'From date is required')
    if (toDate && !to) nextErrors.to = text('payments.statement.validation.toInvalid', 'To date is invalid')
    if (from && to) {
      const fromTimestamp = Date.UTC(from.year, from.month - 1, from.day)
      const toTimestamp = Date.UTC(to.year, to.month - 1, to.day)
      if (fromTimestamp > toTimestamp) nextErrors.to = text('payments.statement.validation.fromBeforeTo', 'From date must be on or before To date')
    }
    setErrors(nextErrors)
    if (nextErrors.from) window.setTimeout(() => fromRef.current?.focus(), 0)
    else if (nextErrors.to) window.setTimeout(() => toRef.current?.focus(), 0)
    return Object.keys(nextErrors).length === 0
  }

  const handleGenerate = async () => {
    if (downloadDisabled || isSubmitting) return
    if (!effectiveStudentIds.length) {
      showAlert({ text: text('payments.statement.alert.noStudents', 'Select at least one student to generate a statement'), status: 'error' }, dispatch)
      return
    }
    if (!validateCustomRange()) return

    setIsSubmitting(true)
    const now = new Date()
    const periodParams = buildStatementPeriodParams({ period, now, fromDate, toDate })
    const results = await Promise.allSettled(effectiveStudentIds.map((userId) => downloadStatement({
      userId,
      parentUserId: effectiveParentUserId || undefined,
      ...periodParams
    })))
    setIsSubmitting(false)

    const failedStudentIds = effectiveStudentIds.filter((id, index) => results[index].status === 'rejected')
    const failureMessages = [...new Set(results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason?.message)
      .filter(Boolean))]
    const successCount = effectiveStudentIds.length - failedStudentIds.length
    if (!failedStudentIds.length) {
      showAlert({ text: text('payments.statement.alert.success', `Generated ${successCount} statement(s).`, { count: successCount }), status: 'success' }, dispatch)
      resetPanel()
    } else if (successCount) {
      showAlert({ text: failureMessages.join('\n') || text('payments.statement.alert.partial', `Generated ${successCount}; failed for: ${failedStudentIds.join(', ')}.`, { successCount, ids: failedStudentIds.join(', ') }), status: 'error' }, dispatch)
    } else {
      showAlert({ text: failureMessages.join('\n') || text('payments.statement.alert.failure', `Couldn't generate statements for: ${failedStudentIds.join(', ')}.`, { ids: failedStudentIds.join(', ') }), status: 'error' }, dispatch)
    }
  }

  if (!getFirstNonEmptyId(query?.parentUserId) && !getFirstNonEmptyId(query?.userId)) return null

  return (
    <div className='znk-statement' ref={rootRef}>
      <button
        type='button'
        className='znk-statement__trigger'
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-controls='statement-account-panel'
        disabled={isGenerating}
        onClick={() => (isOpen ? closePanel() : setIsOpen(true))}
      >
        <IconDocument size={15} aria-hidden='true' />
        <span>{text('payments.statement.trigger', 'Statement')}</span>
        <span className='znk-statement__chevron' aria-hidden='true'>▾</span>
      </button>

      {isOpen && (
        <section id='statement-account-panel' className='znk-statement__panel' role='dialog' aria-label={text('payments.statement.periodTitle', 'Statement period')}>
          <div className='znk-statement__panel-heading'>{text('payments.statement.periodTitle', 'Statement period')}</div>
          <div className='znk-statement__options' role='radiogroup' aria-label={text('payments.statement.periodTitle', 'Statement period')}>
            {periods.map((option) => {
              const subtitle = option.value === 'schoolYear'
                ? getSchoolYearSubtitle(new Date())
                : option.subtitle
              return (
                <label className={`znk-statement__option ${period === option.value ? 'is-checked' : ''}`} key={option.value}>
                  <input
                    type='radio'
                    name='statement-period'
                    value={option.value}
                    checked={period === option.value}
                    disabled={isGenerating}
                    onChange={() => {
                      setPeriod(option.value)
                      setErrors({})
                    }}
                  />
                  <span className='znk-statement__radio' aria-hidden='true' />
                  <span className='znk-statement__option-text'>
                    <span className='znk-statement__option-title'>
                      {text(`payments.statement.periods.${option.value}.title`, option.title)}
                      {option.recommended && <span className='znk-statement__flag'>{text('payments.statement.recommended', 'Recommended')}</span>}
                    </span>
                    {subtitle && <span className='znk-statement__option-sub'>{option.value === 'schoolYear' ? subtitle : text(`payments.statement.periods.${option.value}.subtitle`, subtitle)}</span>}
                  </span>
                </label>
              )
            })}
          </div>

          {period === 'custom' && (
            <div className='znk-statement__custom-range'>
              <label htmlFor='statement-from-date'>
                {text('payments.statement.fromDate', 'From date')}
                <input
                  id='statement-from-date'
                  ref={fromRef}
                  type='date'
                  value={fromDate}
                  disabled={isGenerating}
                  aria-invalid={Boolean(errors.from)}
                  aria-describedby={errors.from ? 'statement-from-date-error' : undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              </label>
              {errors.from && <span id='statement-from-date-error' className='znk-statement__field-error'>{errors.from}</span>}
              <label htmlFor='statement-to-date'>
                <span>{text('payments.statement.toDate', 'To date')}</span>
                <span className='znk-statement__optional'>{text('payments.statement.optional', 'Optional')}</span>
                <input
                  id='statement-to-date'
                  ref={toRef}
                  type='date'
                  value={toDate}
                  disabled={isGenerating}
                  aria-invalid={Boolean(errors.to)}
                  aria-describedby={errors.to ? 'statement-to-date-error' : undefined}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </label>
              {errors.to && <span id='statement-to-date-error' className='znk-statement__field-error'>{errors.to}</span>}
            </div>
          )}
          <div className='znk-statement__divider' />
          <button
            type='button'
            className='znk-statement__download'
            disabled={downloadDisabled}
            onClick={handleGenerate}
          >
            {isGenerating ? <IconLoad size={15} className='znk-statement__loading-icon' aria-hidden='true' /> : <IconDocument size={15} aria-hidden='true' />}
            <span>{isGenerating ? text('payments.statement.generating', 'Generating…') : text('payments.statement.download', 'Download PDF')}</span>
          </button>
        </section>
      )}
    </div>
  )
}

export default StatementAccount
